import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, AlertTriangle, Megaphone, Info, Pin } from "lucide-react";
import api from "@/lib/api";
import { resolveContentHtml, extractFirstImageUrl } from "@/lib/url";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";

const META = {
    urgent: { label: "Pilne", Icon: AlertTriangle, cls: "bg-red-100 text-red-800 border-red-200" },
    important: { label: "Ważne", Icon: Megaphone, cls: "bg-amber-100 text-amber-800 border-amber-200" },
    normal: { label: "Informacja", Icon: Info, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
};

export default function KomunikatDetailPage() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/announcements/${id}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    const meta = item ? (META[item.importance] || META.normal) : META.normal;
    const Icon = meta.Icon;

    const previewText = item ? (item.content || "").replace(/<[^>]*>/g, "").slice(0, 160) : "";
    const ogImage = item ? extractFirstImageUrl(item.content || "") : undefined;
    useSeo({ title: item?.title, description: previewText, image: ogImage, type: "article" });

    return (
        <div data-testid="komunikat-detail-page" className="min-h-screen bg-[#FDFDFD]">
            <Navbar />
            <header className="bg-[#092E22] text-white pt-32 pb-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <Link to="/#komunikaty" data-testid="back-to-komunikaty" className="inline-flex items-center gap-2 text-[#D4A537] text-sm hover:underline mb-6">
                        <ArrowLeft size={14} /> Powrót do komunikatów
                    </Link>
                    {loading && <p className="text-white/70">Wczytywanie...</p>}
                    {notFound && <p className="text-white/70">Nie znaleziono komunikatu.</p>}
                    {item && (
                        <>
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${meta.cls} text-xs font-semibold`}>
                                    <Icon size={12} /> {meta.label}
                                </span>
                                {item.pinned && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4A537] text-[#092E22] text-xs font-bold">
                                        <Pin size={11} /> Przypięte
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1.5 text-white/70 text-xs">
                                    <Calendar size={12} /> {formatDate(item.created_at)}
                                </span>
                            </div>
                            <h1 className="font-serif text-4xl md:text-5xl leading-tight">{item.title}</h1>
                        </>
                    )}
                </div>
            </header>

            {item && (
                <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                    <motion.article
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12"
                        data-testid="komunikat-content"
                    >
                        <div className="rich-content text-gray-700 leading-relaxed text-base md:text-lg" dangerouslySetInnerHTML={{ __html: resolveContentHtml(item.content) || "" }} />
                    </motion.article>
                </main>
            )}

            <Footer />
        </div>
    );
}
